import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Receipt, Loader2, Check, X, Trash2, Clock, ImageIcon, Wallet, User, Download } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { api, fileUrl } from "../lib/api";
import { saveBlob } from "../lib/download";
import { Money } from "./SaudiRiyal";

const money = (n) => <Money value={n} />;
const STATUS = {
  pending: { label: "قيد المراجعة", cls: "bg-amber-100 text-amber-800", Icon: Clock },
  approved: { label: "معتمدة", cls: "bg-emerald-100 text-emerald-700", Icon: Check },
  rejected: { label: "مرفوضة", cls: "bg-rose-100 text-rose-700", Icon: X },
};

const ReviewDialog = ({ item, onClose, onDone }) => {
  const [note, setNote] = useState(item.note || "");
  const [month, setMonth] = useState(item.month || "");
  const [busy, setBusy] = useState(false);
  const act = async (status) => {
    setBusy(true);
    try { await api.reviewPurchase(item.id, { status, note, month }); toast.success(status === "approved" ? "تم اعتماد الفاتورة وإضافتها للراتب" : "تم رفض الفاتورة"); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر التنفيذ"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg font-body max-h-[90vh] overflow-y-auto" dir="rtl" data-testid="purchase-review-dialog">
        <DialogHeader className="text-start"><DialogTitle className="font-heading text-lg flex items-center gap-2"><Receipt className="h-5 w-5 text-blue-600" /> مراجعة فاتورة · {item.employee_name}</DialogTitle>
          <DialogDescription className="text-start">المبلغ: <b className="text-slate-800 font-mono-nums">{money(item.amount)}</b> · {item.description || "—"}</DialogDescription></DialogHeader>
        <a href={fileUrl(item.photo_id)} target="_blank" rel="noreferrer" className="block rounded-xl overflow-hidden border border-slate-200 bg-slate-50">
          <img src={fileUrl(item.photo_id)} alt="فاتورة" className="w-full max-h-72 object-contain" data-testid="purchase-photo" />
        </a>
        <div className="grid grid-cols-2 gap-3 mt-1">
          <label className="block"><span className="text-[11px] text-slate-500">شهر الإضافة للراتب</span>
            <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-9 text-sm" data-testid="purchase-month" /></label>
          <label className="block"><span className="text-[11px] text-slate-500">ملاحظة (اختياري)</span>
            <Input value={note} onChange={(e) => setNote(e.target.value)} className="h-9 text-sm" data-testid="purchase-note" /></label>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => act("rejected")} disabled={busy} data-testid="purchase-reject" className="text-rose-600 border-rose-200 hover:bg-rose-50 gap-1.5"><X className="h-4 w-4" /> رفض</Button>
          <Button onClick={() => act("approved")} disabled={busy} data-testid="purchase-approve" className="bg-emerald-600 hover:bg-emerald-700 gap-1.5">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} اعتماد وإضافة للراتب</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const PurchasesPanel = ({ canEdit }) => {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("pending");
  const [review, setReview] = useState(null);
  const [summary, setSummary] = useState(null);
  const [sumMonth, setSumMonth] = useState(() => new Date().toISOString().slice(0, 7));

  const load = useCallback(async () => {
    try { setData(await api.listPurchases(filter === "all" ? {} : { status: filter })); }
    catch { toast.error("تعذّر تحميل المشتريات"); setData({ purchases: [], counts: {} }); }
  }, [filter]);
  const loadSummary = useCallback(async () => {
    try { setSummary(await api.purchasesSummary({ month: sumMonth })); }
    catch { setSummary({ month: sumMonth, grand_total: 0, employees: [] }); }
  }, [sumMonth]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (canEdit) loadSummary(); }, [loadSummary, canEdit]);

  const del = async (it) => { if (!window.confirm("حذف هذه الفاتورة؟")) return; try { await api.deletePurchase(it.id); load(); loadSummary(); } catch { toast.error("تعذّر الحذف"); } };

  const [exporting, setExporting] = useState(false);
  const onExport = async () => {
    setExporting(true);
    try {
      const blob = await api.exportPurchases(sumMonth);
      await saveBlob(blob, `purchases_${sumMonth}.xlsx`);
    } catch { toast.error("تعذّر تصدير التقرير"); }
    finally { setExporting(false); }
  };

  if (!data) return <div className="py-16 text-center text-slate-300"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></div>;
  const c = data.counts || {};
  const tabs = [["pending", `قيد المراجعة (${c.pending || 0})`], ["approved", `معتمدة (${c.approved || 0})`], ["rejected", `مرفوضة (${c.rejected || 0})`], ["all", "الكل"]];

  return (
    <div className="space-y-4" data-testid="purchases-panel" dir="rtl">
      <div className="flex items-center gap-2">
        <div className="grid h-9 w-9 place-items-center rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 text-white"><Receipt className="h-5 w-5" /></div>
        <div><h3 className="font-heading font-bold text-slate-800">مشتريات المندوب</h3>
          <p className="text-[11px] text-slate-400">فواتير اشتراها المندوب على حسابه — راجعها واعتمدها لتُضاف إلى راتبه. الاعتماد يدوي.</p></div>
      </div>

      {canEdit && summary && (
        <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3" data-testid="purchases-summary">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2">
              <div className="grid h-8 w-8 place-items-center rounded-lg bg-emerald-100 text-emerald-700"><Wallet className="h-4 w-4" /></div>
              <h4 className="font-heading font-bold text-slate-800 text-sm">إجمالي المشتريات المعتمدة</h4>
            </div>
            <label className="flex items-center gap-1.5 text-[11px] text-slate-500">شهر الاعتماد
              <Input type="month" value={sumMonth} onChange={(e) => setSumMonth(e.target.value)} className="h-8 w-40 text-sm" data-testid="summary-month" /></label>
          </div>
          <div className="flex justify-start">
            <Button onClick={onExport} disabled={exporting || summary.employees.length === 0} variant="outline" size="sm" data-testid="purchases-export" className="gap-1.5 text-emerald-700 border-emerald-200 hover:bg-emerald-50">
              {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} تصدير التقرير (Excel)
            </Button>
          </div>
          <div className="rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white p-4 flex items-center justify-between" data-testid="summary-grand-total">
            <span className="text-sm opacity-90">الإجمالي العام لهذا الشهر</span>
            <span className="font-heading font-bold text-2xl font-mono-nums">{money(summary.grand_total)}</span>
          </div>
          {summary.employees.length === 0 ? (
            <p className="text-center text-slate-400 text-xs py-3">لا توجد مشتريات معتمدة في هذا الشهر.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
              {summary.employees.map((e) => (
                <div key={e.employee_id} data-testid={`summary-emp-${e.employee_id}`} className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-blue-100 text-blue-600"><User className="h-4 w-4" /></div>
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-800 text-sm truncate">{e.employee_name || "—"}</p>
                      <p className="text-[10px] text-slate-400">{e.count} فاتورة{e.branch ? ` · ${e.branch}` : ""}</p>
                    </div>
                  </div>
                  <span className="font-mono-nums font-bold text-emerald-700 shrink-0 text-sm">{money(e.total)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="flex items-center gap-1 rounded-lg bg-slate-100 p-1 w-fit overflow-x-auto">
        {tabs.map(([k, lbl]) => (
          <button key={k} data-testid={`purchases-filter-${k}`} onClick={() => setFilter(k)} className={`px-3 py-1.5 text-sm rounded-md whitespace-nowrap transition ${filter === k ? "bg-white shadow-sm font-semibold text-slate-800" : "text-slate-500"}`}>{lbl}</button>
        ))}
      </div>

      {data.purchases.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 py-14 text-center" data-testid="purchases-empty">
          <Receipt className="mx-auto h-10 w-10 text-slate-300" />
          <p className="mt-2 text-slate-500 text-sm">لا توجد فواتير في هذه الحالة.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.purchases.map((it) => {
            const st = STATUS[it.status] || STATUS.pending;
            return (
              <div key={it.id} data-testid={`purchase-card-${it.id}`} className="flex flex-col rounded-2xl border border-slate-200 bg-white overflow-hidden">
                <button onClick={() => canEdit && setReview(it)} className="relative h-36 bg-slate-100 flex items-center justify-center">
                  {it.photo_id ? <img src={fileUrl(it.photo_id)} alt="فاتورة" className="h-full w-full object-cover" /> : <ImageIcon className="h-10 w-10 text-slate-300" />}
                  <span className={`absolute top-2 right-2 inline-flex items-center gap-1 rounded-full text-[10px] font-semibold px-2 py-0.5 ${st.cls}`}><st.Icon className="h-3 w-3" />{st.label}</span>
                </button>
                <div className="flex-1 p-3">
                  <div className="flex items-center justify-between">
                    <p className="font-semibold text-slate-800 truncate">{it.employee_name}</p>
                    <span className="font-mono-nums font-bold text-emerald-700 shrink-0">{money(it.amount)}</span>
                  </div>
                  {it.description && <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{it.description}</p>}
                  <p className="text-[10px] text-slate-400 mt-1">{it.branch ? `${it.branch} · ` : ""}شهر {it.month} · {(it.created_at || "").slice(0, 10)}</p>
                  {it.note && <p className="text-[11px] text-slate-500 mt-1">ملاحظة: {it.note}</p>}
                </div>
                {canEdit && (
                  <div className="flex border-t border-slate-100">
                    <button onClick={() => setReview(it)} data-testid={`purchase-review-${it.id}`} className="flex-1 py-2 text-sm text-blue-600 hover:bg-blue-50 font-semibold">مراجعة</button>
                    <button onClick={() => del(it)} data-testid={`purchase-del-${it.id}`} className="w-12 grid place-items-center text-rose-500 hover:bg-rose-50 border-r border-slate-100"><Trash2 className="h-4 w-4" /></button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {review && <ReviewDialog item={review} onClose={() => setReview(null)} onDone={() => { setReview(null); load(); loadSummary(); }} />}
    </div>
  );
};
